/*
 * @providesModule UserPropertiesConstants
 *
 */

/** START: db Keys */

/**
 * These are the keys that are currently used in the db when we call user/v1/properties.
 * This API is entirely client-driven, meaning that the client can create and update any
 * key-value pair it wants. The server will store and return the keys when requested, and does
 * not care about what the keys actually are. Therefore, the client has to be very careful
 * about the keys that it uses and should do everything to prevent key collisions.
 * This file is a list of all the keys that are currently used in the db. If you want to add
 * a new key please add it here, add a comment for what it's used for, and make sure that it
 * is unique. Then only "use" a key / group of keys by calling the variables from this file.
 */

//*************************
//          FTUE
//*************************

/** Represents the user's completion status for the Store FTUE. */
export const FTUE_STORE_KEY = 'ftue.store';
/** Represents the user's completion status for the Progressive Leagues FTUE. */
export const FTUE_PROGRESSIVE_LEAGUES_KEY = 'ftue.progressiveLeagues';
/** Represents the user's completion status for the Global Leagues MS1 FTUE. */
export const FTUE_GLOBAL_LEAGUES_MS1_KEY = 'ftue.globalLeaguesMS1';
/** Represents the user's completion status for the Progressive Leagues V2 FTUE. */
export const FTUE_PROGRESSIVE_LEAGUES_V2_KEY = 'ftue.progressiveLeaguesV2';
/** Represents the user's completion status for the Progressive Leagues V2 Milestone FTUE. */
export const FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY = 'ftue.progressiveLeaguesV2Milestone';
/** Represents the user's completion status for the Trophies FTUE. */
export const FTUE_TROPHIES_KEY = 'ftue.trophies';
/** Represents the user's completion status for the Play Again FTUE. */
export const FTUE_PLAY_AGAIN_KEY = 'ftue.playAgain';
/** Represents the user's completion status for the Interstitial FTUE. */
export const FTUE_INTERSTITIAL_KEY = 'ftue.interstitial';
/** Represents the user's completion status for the Levels FTUE. */
export const FTUE_LEVELS_KEY = 'ftue.levels';
/** Represents the user's completion status for the Async Brackets FTUE. */
export const FTUE_ASYNC_BRACKETS_KEY = 'ftue.asyncBrackets';

//*************************
//          WITHDRAWAL
//*************************
/**
 * Represents if the user's first time seen the BankWithdrawalPopup
 * @type string
 * @example "2023-10-01T00:00:00Z"
 **/
export const WITHDRAWAL_LAST_TIME_SEEN_BANK_POPUP_KEY = 'withdrawal.lastTimeSeenBankPopup';
export const WITHDRAWAL_FIRST_TIME_DEPOSITOR_SEEN_BANK_POPUP_KEY =
  'withdrawal.firstTimeDepositorHasSeenBankPopup';

/** END: db Keys */

/** START: Type Management */

/**
 * Use this section to handle any type management required for the above keys.
 * As an example, FtueKeys is used to create a union type of all the FTUE keys. This is then
 * used to create the UserPropertiesData type in UserPropertiesController, representing the
 * full scope of key-value pairs that can be accessed from the db.
 */

//*************************
//          FTUE
//*************************

/** All of the FTUE keys used for API interaction. Add to this list if any more items are added to the db keys list above */
export const FTUE_KEYS_ALL = [
  FTUE_STORE_KEY,
  FTUE_PROGRESSIVE_LEAGUES_KEY,
  FTUE_PROGRESSIVE_LEAGUES_V2_KEY,
  FTUE_PROGRESSIVE_LEAGUES_V2_MILESTONE_KEY,
  FTUE_GLOBAL_LEAGUES_MS1_KEY,
  FTUE_TROPHIES_KEY,
  FTUE_PLAY_AGAIN_KEY,
  FTUE_INTERSTITIAL_KEY,
  FTUE_LEVELS_KEY,
  FTUE_ASYNC_BRACKETS_KEY,
] as const;

/** All of the FTUE keys as a union type. */
export type FtueKeys = (typeof FTUE_KEYS_ALL)[number];

//*************************
//          WITHDRAWAL
//*************************
export const WITHDRAWAL_KEYS_ALL = [
  WITHDRAWAL_LAST_TIME_SEEN_BANK_POPUP_KEY,
  WITHDRAWAL_FIRST_TIME_DEPOSITOR_SEEN_BANK_POPUP_KEY,
] as const;
export type WithdrawalKeys = (typeof WITHDRAWAL_KEYS_ALL)[number];

/** All of the accepted keys as a union type. Used to set up the UserPropertiesData type in UserPropertiesController. */
export type AllUserPropertiesKeys = FtueKeys | WithdrawalKeys; // Add more keys as needed

/** END: Type Management */
